/**
 * Query keys tập trung — tránh trùng/lệch key giữa các feature (TanStack Query best practice).
 * Mỗi feature mở rộng namespace của mình tại đây.
 */
export const queryKeys = {
  systemSettings: {
    all: ["system-settings"] as const,
    list: (params?: unknown) => ["system-settings", "list", params ?? {}] as const,
  },
  locale: {
    all: ["locale"] as const,
    list: () => ["locale", "list"] as const,
  },
  allowlist: {
    // US-AE-IMP.F4-0114 (@UC9144) — trình quản lý allowlist domain admin (đọc + PATCH admin_allowed_domains).
    all: ["allowlist"] as const,
    list: () => ["allowlist", "list"] as const,
  },
  members: {
    all: ["members"] as const,
    list: (cursor?: string, limit?: number) =>
      ["members", "list", cursor ?? null, limit ?? null] as const,
  },
  moneyPolicy: {
    all: ["money-policy"] as const,
    list: () => ["money-policy", "list"] as const,
  },
  featureFlags: {
    all: ["feature-flags"] as const,
    detail: (key: string) => ["feature-flags", "detail", key] as const,
  },
  search: {
    all: ["search"] as const,
    query: (q: string, cursor?: string, limit?: number) =>
      ["search", "query", q, cursor ?? null, limit ?? null] as const,
  },
  users: {
    all: ["users"] as const,
    list: (q: string, cursor?: string, limit?: number) =>
      ["users", "list", q, cursor ?? null, limit ?? null] as const,
    detail: (id: string) => ["users", "detail", id] as const,
    social: (id: string) => ["users", "social", id] as const,
  },
  identity: {
    all: ["identity"] as const,
    lookup: (key: string) => ["identity", "lookup", key] as const,
  },
  configOverrides: {
    all: ["config-overrides"] as const,
    list: (params?: unknown) => ["config-overrides", "list", params ?? {}] as const,
  },
  groups: {
    all: ["groups"] as const,
    list: (q?: string, cursor?: string, limit?: number, state?: string) =>
      ["groups", "list", q ?? null, cursor ?? null, limit ?? null, state ?? null] as const,
    detail: (id: string) => ["groups", "detail", id] as const,
    health: (id: string) => ["groups", "health", id] as const,
    configAudit: (id: string, cursor?: string) =>
      ["groups", "config-audit", id, cursor ?? null] as const,
    // US-E11.F1-0064 (@UC0071) — per-group AI enable/disable toggle (read + PATCH).
    aiConfig: (id: string) => ["groups", "ai-config", id] as const,
  },
  funds: {
    // US-AE-IMP2-T10 (@UC #7c) — admin-scope quỹ (list + detail, READ-ONLY).
    all: ["funds"] as const,
    list: (q?: string, cursor?: string, groupId?: string, limit?: number) =>
      ["funds", "list", q ?? null, cursor ?? null, groupId ?? null, limit ?? null] as const,
    detail: (id: string) => ["funds", "detail", id] as const,
    // US-AIF.P0-0244 (@UC9416) — config hiệu lực per-fund (fund>group>system provenance), READ-ONLY.
    effectiveConfig: (id: string) => ["funds", "effective-config", id] as const,
  },
  reconcile: {
    all: ["reconcile"] as const,
    board: (params?: unknown) => ["reconcile", "board", params ?? {}] as const,
  },
  // US-E18.F6-0199 (@UC9292–@UC9295): đối soát ĐA KÊNH (valueForm) + config tỷ giá (read).
  reconcileChannels: {
    all: ["reconcile-channels"] as const,
    board: (params?: unknown) => ["reconcile-channels", "board", params ?? {}] as const,
    fxConfig: () => ["reconcile-channels", "fx-config"] as const,
  },
  drift: {
    all: ["drift"] as const,
    list: (params?: unknown) => ["drift", "list", params ?? {}] as const,
    doubleEntry: () => ["drift", "double-entry"] as const,
  },
  idempotency: {
    all: ["idempotency"] as const,
    list: (params?: unknown) => ["idempotency", "list", params ?? {}] as const,
  },
  cashflow: {
    all: ["cashflow"] as const,
    summary: (params?: unknown) => ["cashflow", "summary", params ?? {}] as const,
  },
  dashboard: {
    all: ["dashboard"] as const,
    overview: () => ["dashboard", "overview"] as const,
    queues: () => ["dashboard", "queues"] as const,
  },
  metricsSeries: {
    all: ["metricsSeries"] as const,
    series24h: () => ["metricsSeries", "24h"] as const,
  },
  aiConversations: {
    all: ["aiConversations"] as const,
    list: () => ["aiConversations", "list"] as const,
    detail: (id: string) => ["aiConversations", "detail", id] as const,
  },
  payments: {
    all: ["payments"] as const,
    gateway: () => ["payments", "gateway"] as const,
    webhookEvents: (params?: unknown) => ["payments", "webhook-events", params ?? {}] as const,
    // US-AE6.F3-0022 (@UC9052 deadletter · @UC9054 reconcile)
    deadletter: (params?: unknown) => ["payments", "deadletter", params ?? {}] as const,
    reconcile: (params?: unknown) => ["payments", "reconcile", params ?? {}] as const,
  },
  outbox: {
    all: ["outbox"] as const,
    // US-AE7.F1-0023 (@UC9060/@UC9061)
    list: (params?: unknown) => ["outbox", "list", params ?? {}] as const,
    channels: () => ["outbox", "channels"] as const,
  },
  channelHealth: {
    all: ["channel-health"] as const,
    // US-AE7.F3-0024 (@UC9062/@UC9063/@UC9065)
    health: () => ["channel-health", "health"] as const,
    znsTemplates: () => ["channel-health", "zns-templates"] as const,
  },
  notificationTemplates: {
    all: ["notification-templates"] as const,
    // US-AE7.F5-0025 (@UC9064)
    list: () => ["notification-templates", "list"] as const,
    // US-AE-IMP.F5-0117 (@UC9147) — preview render + validate theo event×locale×channel.
    preview: (eventType: string, locale?: string, channel?: string) =>
      ["notification-templates", "preview", eventType, locale ?? "", channel ?? ""] as const,
  },
  aiCost: {
    all: ["ai-cost"] as const,
    // US-AE8.F1-0026 (@UC9070/@UC9071)
    budget: () => ["ai-cost", "budget"] as const,
    usage: (cursor?: string) => ["ai-cost", "usage", cursor ?? null] as const,
  },
  aiWriteStatus: {
    // US-AIF.P0-0245 (@UC9417) — aggregate 3-tier kill-switch AI ghi tiền (env + flag + group).
    all: ["ai-write-status"] as const,
    status: () => ["ai-write-status", "status"] as const,
    // US-AI-0294 (@UC9417) — danh sách nhóm ĐANG BẬT AI (tầng 3 màn "Cấu hình AI").
    enabledGroups: () => ["ai-write-status", "enabled-groups"] as const,
  },
  aiEval: {
    all: ["ai-eval"] as const,
    // US-AE8.F3-0027 (@UC9072/@UC9074)
    runs: (cursor?: string) => ["ai-eval", "runs", cursor ?? null] as const,
    modelConfig: () => ["ai-eval", "model-config"] as const,
  },
  aiFlows: {
    // US-AI.F5-0234 (@UC9400 @AI-TRU-01) — config flow AI (list + override) + preview sandbox.
    all: ["ai-flows"] as const,
    list: (groupId?: string) => ["ai-flows", "list", groupId ?? null] as const,
    actors: () => ["ai-flows", "preview", "actors"] as const,
  },
  aiCapabilities: {
    // US-AIF.P0-0243 (@UC9415 · @gate config,ai) — danh mục usecase AI-write bật/tắt.
    all: ["ai-capabilities"] as const,
    list: (groupId?: string) => ["ai-capabilities", "list", groupId ?? null] as const,
  },
  aiReadCapabilities: {
    // US-AI-0322 (@UC9415 @UC9400) — danh mục năng lực AI ĐỌC. Namespace RIÊNG: không có mutation nào
    // invalidate nó (READ-ONLY, luôn bật) ⇒ gộp chung `ai-capabilities` sẽ refetch vô ích mỗi lần
    // bật/tắt một usecase ghi.
    all: ["ai-read-capabilities"] as const,
    list: () => ["ai-read-capabilities", "list"] as const,
  },
  aiDeepLinks: {
    // US-AI-0310-S06 (@UC9400 · @gate config) — config deep-link AI theo iOS/Android/Web.
    all: ["ai-deep-links"] as const,
    list: () => ["ai-deep-links", "list"] as const,
  },
  aiProvider: {
    all: ["ai-provider"] as const,
    // US-AE-IMP.F6-0119 (@UC9149) — status key nhà cung cấp (configured/keyLast4 — KHÔNG key thô).
    status: () => ["ai-provider", "status"] as const,
  },
  aiAudit: {
    all: ["ai-audit"] as const,
    // US-AE8.F4-0028 (@UC9073/@UC9075)
    trace: (cursor?: string) => ["ai-audit", "trace", cursor ?? null] as const,
    safety: (cursor?: string) => ["ai-audit", "safety", cursor ?? null] as const,
    // US-AE-IMP2-T11-S6 (@UC9156) — detail 1 mục truy vết (read-only).
    traceDetail: (id: string) => ["ai-audit", "trace", "detail", id] as const,
  },
  observability: {
    all: ["observability"] as const,
    // US-AE9.F1-0029 (@UC9080/@UC9081)
    snapshot: () => ["observability", "snapshot"] as const,
    alertRules: (params?: unknown) => ["observability", "alert-rules", params ?? {}] as const,
  },
  securityThreats: {
    all: ["security-threats"] as const,
    // US-AE9.F3-0030 (@UC9082)
    board: (params?: unknown) => ["security-threats", "board", params ?? {}] as const,
  },
  securityConsole: {
    all: ["security-console"] as const,
    // US-E19.F7-0206 (@UC9320) — read tổng hợp (mutation can-thiệp KHÔNG có query key: invalidate all).
    read: () => ["security-console", "read"] as const,
  },
  riskReview: {
    all: ["risk-review"] as const,
    // US-AE12.F1-0042 (@UC9110/@UC9113) — hàng đợi review rủi ro.
    queue: (params?: unknown) => ["risk-review", "queue", params ?? {}] as const,
  },
  moderation: {
    all: ["moderation"] as const,
    // US-AE12.F2-0043 (@UC9111/@UC9112) — báo cáo lạm dụng + kiểm duyệt nội dung.
    reports: (params?: unknown) => ["moderation", "reports", params ?? {}] as const,
    items: (params?: unknown) => ["moderation", "items", params ?? {}] as const,
  },
  collectionsOversight: {
    all: ["collections-oversight"] as const,
    // US-AE4.F5-0091 (@UC9142) — giám sát đợt thu xuyên quỹ/nhóm (READ-ONLY, cursor + filter).
    list: (params?: unknown) => ["collections-oversight", "list", params ?? {}] as const,
    // US-AE-IMP2-T11-S4 (@UC9154) — detail 1 đợt thu (read-only).
    detail: (id: string) => ["collections-oversight", "detail", id] as const,
  },
  privacyRequests: {
    all: ["privacy-requests"] as const,
    // US-AE3.F7-0014 (@UC9026) — hàng đợi yêu cầu PII/GDPR (list + fulfill export/deletion).
    list: (params?: unknown) => ["privacy-requests", "list", params ?? {}] as const,
  },
  votingOversight: {
    all: ["voting-oversight"] as const,
    // US-AE5.F5-0092 (@UC9143) — giám sát phiên biểu quyết xuyên quỹ/nhóm (READ-ONLY, cursor + filter).
    list: (params?: unknown) => ["voting-oversight", "list", params ?? {}] as const,
    // US-AE-IMP2-T11-S5 (@UC9155) — detail 1 phiên (read-only, aggregate).
    detail: (id: string) => ["voting-oversight", "detail", id] as const,
  },
  expensesOversight: {
    all: ["expenses-oversight"] as const,
    // US-AE5.F6-0093 (@UC9164) — giám sát TOÀN BỘ khoản chi xuyên quỹ (READ-ONLY; filter server-side + full-text).
    list: (params?: unknown) => ["expenses-oversight", "list", params ?? {}] as const,
  },
  // US-E21.F7-0219 (@UC9202–@UC9204) — chi tiết chia suất 1 khoản chi + can thiệp (REVERSAL+repost).
  expenseSplits: {
    all: ["expense-splits"] as const,
    detail: (expenseId: string) => ["expense-splits", "detail", expenseId] as const,
  },
  // US-E20.F6-0212 (@UC9343–@UC9345) — oversight + can thiệp 5 cỗ máy chuyên biệt.
  adminMachines: {
    all: ["admin-machines"] as const,
    hui: (params?: unknown) => ["admin-machines", "hui", params ?? {}] as const,
    reciprocal: (params?: unknown) => ["admin-machines", "reciprocal", params ?? {}] as const,
    earmark: (params?: unknown) => ["admin-machines", "earmark", params ?? {}] as const,
    invest: (params?: unknown) => ["admin-machines", "invest", params ?? {}] as const,
    pools: (params?: unknown) => ["admin-machines", "pools", params ?? {}] as const,
    navHistory: (positionId?: string) => ["admin-machines", "nav-history", positionId ?? ""] as const,
  },
  debtsOversight: {
    all: ["debts-oversight"] as const,
    // US-E15.F6-0181 (@UC9227-9230) — giám sát nợ/khoản vay xuyên quỹ (READ + netting) + can thiệp.
    list: (params?: unknown) => ["debts-oversight", "list", params ?? {}] as const,
    netting: (fundId?: string) => ["debts-oversight", "netting", fundId ?? ""] as const,
  },
  broadcasts: {
    all: ["broadcasts"] as const,
    // US-AE13.F1-0044 (@UC9120) — broadcast & in-app banner (list lịch sử).
    list: (params?: unknown) => ["broadcasts", "list", params ?? {}] as const,
  },
  billing: {
    all: ["billing"] as const,
    // US-AE14.F1-0046 (@UC9130 plan-catalog · @UC9132 entitlement) — danh mục gói + subscription nhóm.
    plans: (params?: unknown) => ["billing", "plans", params ?? {}] as const,
    subscription: (groupId: string) => ["billing", "subscription", groupId] as const,
    // US-AE14.F2-0047 (@UC9131 usage-metering · @UC9133 unit-economics) — read-only.
    usage: (params?: unknown) => ["billing", "usage", params ?? {}] as const,
    economics: (params?: unknown) => ["billing", "economics", params ?? {}] as const,
  },
  transparency: {
    all: ["transparency"] as const,
    // US-AE13.F2-0045 (@UC9121 link-ops · @UC9122 quiet-hours) — link oversight toàn hệ + revoke + quiet-hours VN.
    links: (params?: unknown) => ["transparency", "links", params ?? {}] as const,
    quietHours: () => ["transparency", "quiet-hours"] as const,
  },
  auditLog: {
    all: ["audit-log"] as const,
    // US-AE9.F4-0031 (@UC9083)
    list: (params?: unknown) => ["audit-log", "list", params ?? {}] as const,
  },
  sre: {
    all: ["sre"] as const,
    // US-AE9.F5-0032 (@UC9084/@UC9085)
    backups: (params?: unknown) => ["sre", "backups", params ?? {}] as const,
    secrets: (params?: unknown) => ["sre", "secrets", params ?? {}] as const,
  },
  ci: {
    all: ["ci"] as const,
    // US-AE10.F1-0033 (@UC9090/@UC9091)
    pipelines: (params?: unknown) => ["ci", "pipelines", params ?? {}] as const,
    gates: (params?: unknown) => ["ci", "gates", params ?? {}] as const,
  },
  analyticsGrowth: {
    all: ["analytics-growth"] as const,
    // US-AE10.F3-0034 (@UC9092)
    growth: (params?: unknown) => ["analytics-growth", "growth", params ?? {}] as const,
  },
  referrals: {
    all: ["referrals"] as const,
    // DF-9 (@UC9177): list từng ca giới thiệu (resource /referrals) — GET /admin/referrals.
    list: (params?: unknown) => ["referrals", "list", params ?? {}] as const,
  },
  analyticsReferrals: {
    all: ["analytics-referrals"] as const,
    // US-AE10.F4-0035 (@UC9093)
    referrals: (params?: unknown) => ["analytics-referrals", "referrals", params ?? {}] as const,
    cohorts: () => ["analytics-referrals", "cohorts"] as const,
  },
  analyticsGroupHealth: {
    all: ["analytics-group-health"] as const,
    // US-AE10.F5-0037 (@UC9094)
    health: (params?: unknown) => ["analytics-group-health", "health", params ?? {}] as const,
    watchlist: (params?: unknown) => ["analytics-group-health", "watchlist", params ?? {}] as const,
  },
  disputes: {
    all: ["disputes"] as const,
    // US-AE11.F1-0038 (@UC9100/@UC9101)
    list: (params?: unknown) => ["disputes", "list", params ?? {}] as const,
    detail: (id: string) => ["disputes", "detail", id] as const,
  },
  corrections: {
    all: ["corrections"] as const,
    // US-AE11.F5-0041 (@UC9104) — controlled ledger correction 2-người-duyệt.
    list: (params?: unknown) => ["corrections", "list", params ?? {}] as const,
    detail: (id: string) => ["corrections", "detail", id] as const,
    // US-AE-IMP2-T15 (@UC9157) — tra bút toán theo account để prefill targetTxId (gỡ dead-flow).
    ledgerByAccount: (accountId: string) =>
      ["corrections", "ledger-by-account", accountId] as const,
  },
  supportInbox: {
    all: ["support-inbox"] as const,
    // US-AE11.F4-0040 (@UC9103) — hộp thư hỗ trợ Zalo OA + macro tiếng Việt.
    list: (params?: unknown) => ["support-inbox", "list", params ?? {}] as const,
    detail: (id: string) => ["support-inbox", "detail", id] as const,
    macros: () => ["support-inbox", "macros"] as const,
  },
  dictionaries: {
    all: ["dictionaries"] as const,
    // US-AE15.F1-0090 (@UC9140 read · @UC9141 write) — Master Data & Dictionaries (9 resource).
    list: (key: string, cursor?: string) => ["dictionaries", "list", key, cursor ?? null] as const,
  },
  impersonation: {
    all: ["impersonation"] as const,
    // US-AE11.F3-0039 (@UC9102) — phiên "xem như user" read-only + banner active.
    active: () => ["impersonation", "active"] as const,
    // Hồ sơ target user đọc qua token impersonation (panel read-only). Key theo token (đổi phiên → refetch).
    profile: () => ["impersonation", "profile"] as const,
  },
} as const;
